import { db, auth } from '@/firebase';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  runTransaction,
  orderBy,
} from 'firebase/firestore';
import type { UserRole } from '@/services/auth';

// Categoria da instituição: aceitar valores livres (ex.: SAÚDE, EDUCAÇÃO, etc.)
export type Category = string;

export interface Institution {
  id: string;
  name: string;
  category: Category;
  link?: string;
  cnpj: string;
  legalName?: string;
  contact?: { phone?: string; email?: string };
  responsibleAdminUid?: string;
  logoUrl?: string;
  social?: { facebook?: string; instagram?: string; x?: string; tiktok?: string; youtube?: string; whatsapp?: string };
}

export interface Councilor {
  id: string;
  name: string;
  party: string;
  totalLimit: number;
  email?: string;
  photoUrl?: string;
}

export interface Amendment {
  id: string;
  councilorId: string;
  institutionId: string;
  description: string;
  officialCode?: string;
  value: number;
  status: string;
  date: string; // ISO yyyy-mm-dd
  category?: Category;
  classification?: 'Custeio' | 'Investimento' | string;
  objectDetail?: string;
  workPlan?: { projectName?: string; purpose?: string; period?: string };
  attachments?: string[];
  signedLink?: string;
  detailingSaved?: boolean;
  federalStatus?: string;
  dispatchNote?: string;
  dispatchStatus?: 'aprovada' | 'retificar' | 'reprovada';
}

export interface Settings {
  allocationsLocked: boolean;
  lockSchedule?: { enabled: boolean; date: string; time: string };
  institutionFormSchedule?: { enabled: boolean; startDate: string; startTime: string; endDate: string; endTime: string; notify?: boolean; completed?: boolean };
}

// Modelos de descrição para emendas (selecionáveis pelos vereadores)
export interface DescriptionTemplate {
  id: string;
  text: string; // Texto que será inserido no campo descrição
  active?: boolean;
}

export const institutionsCol = collection(db, 'institutions');
export const councilorsCol = collection(db, 'councilors');
export const amendmentsCol = collection(db, 'amendments');
export const settingsDocRef = doc(db, 'system', 'settings');
export const usersCol = collection(db, 'users');
export const descriptionTemplatesCol = collection(db, 'description_templates');
export const violationLogsCol = collection(db, 'rule_violations');
export const adminLogsCol = collection(db, 'admin_logs');
export const uploadLogsCol = collection(db, 'upload_logs');

export async function listInstitutions(): Promise<Institution[]> {
  const snap = await getDocs(institutionsCol);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Institution, 'id'>) }));
}

export async function addInstitution(inst: Omit<Institution, 'id'>) {
  return addDoc(institutionsCol, inst);
}

export async function listCouncilors(): Promise<Councilor[]> {
  const snap = await getDocs(councilorsCol);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Councilor, 'id'>) }));
}

export async function addCouncilor(c: Omit<Councilor, 'id'>) {
  return addDoc(councilorsCol, c);
}

export async function updateCouncilor(id: string, data: Partial<Councilor>) {
  const ref = doc(db, 'councilors', id);
  return updateDoc(ref, data);
}

export async function listAmendmentsByCouncilor(councilorId: string): Promise<Amendment[]> {
  const q = query(amendmentsCol, where('councilorId', '==', councilorId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
}

// Lista todas as emendas (para relatórios agregados)
export async function listAmendments(): Promise<Amendment[]> {
  const snap = await getDocs(amendmentsCol);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
}

export async function listAmendmentsByInstitutionIds(ids: string[]): Promise<Amendment[]> {
  const uniq = Array.from(new Set(ids)).filter(Boolean);
  if (uniq.length === 0) return [];
  const parts = Array.from({ length: Math.ceil(uniq.length / 10) }, (_, i) => uniq.slice(i * 10, (i + 1) * 10));
  const results: Amendment[] = [];
  for (const part of parts) {
    const q = query(amendmentsCol, where('institutionId', 'in', part));
    const snap = await getDocs(q);
    for (const d of snap.docs) results.push({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) });
  }
  return results;
}

export function getInstitutionGroupIdsByCnpj(insts: Institution[], cnpj: string): string[] {
  const target = (cnpj || '').replace(/\D/g, '');
  return insts.filter(x => ((x.cnpj || '').replace(/\D/g, '') === target)).map(x => x.id);
}

export async function addAmendment(a: Omit<Amendment, 'id'>) {
  return addDoc(amendmentsCol, a);
}

export async function updateAmendment(id: string, data: Partial<Amendment>) {
  const ref = doc(db, 'amendments', id);
  return updateDoc(ref, data);
}

export async function deleteAmendment(id: string) {
  const ref = doc(db, 'amendments', id);
  return deleteDoc(ref);
}

export async function getSettings(): Promise<Settings | null> {
  const snap = await getDoc(settingsDocRef);
  return snap.exists() ? (snap.data() as Settings) : null;
}

export async function setSettings(settings: Settings) {
  return setDoc(settingsDocRef, settings, { merge: true });
}

export async function updateInstitution(id: string, data: Partial<Institution>) {
  const ref = doc(db, 'institutions', id);
  return updateDoc(ref, data);
}

export async function deleteInstitution(id: string) {
  const ref = doc(db, 'institutions', id);
  return deleteDoc(ref);
}

export async function getUserRole(uid: string): Promise<UserRole> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return undefined;
  const data = snap.data() as { role?: UserRole };
  return data.role;
}

export async function getUserCouncilorId(uid: string): Promise<string | undefined> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return undefined;
  const data = snap.data() as { councilorId?: string };
  return data.councilorId;
}

export async function getUserInstitutionId(uid: string): Promise<string | undefined> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return undefined;
  const data = snap.data() as { institutionId?: string };
  return data.institutionId;
}

export async function getUserForcePasswordChange(uid: string): Promise<boolean> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const data = snap.data() as { forcePasswordChange?: boolean };
  return !!data.forcePasswordChange;
}

export async function setUserForcePasswordChange(uid: string, value: boolean) {
  const ref = doc(db, 'users', uid);
  await setDoc(ref, { forcePasswordChange: !!value }, { merge: true });
}

export async function getUserCanUploadImages(uid: string): Promise<boolean> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return true;
  const data = snap.data() as { canUploadImages?: boolean };
  return data.canUploadImages !== false;
}

export async function setUserInstitution(uid: string, institutionId: string | undefined) {
  const ref = doc(db, 'users', uid);
  if (institutionId) {
    await setDoc(ref, { institutionId }, { merge: true });
  } else {
    await setDoc(ref, { institutionId: null }, { merge: true });
  }
}

export interface AdminUser { id: string; email?: string; adminLevel?: 'principal' | 'padrao' }

export async function listAdminsClient(): Promise<AdminUser[]> {
  const snap = await getDocs(query(usersCol, where('role', '==', 'admin')));
  return snap.docs.map((d) => ({ id: d.id, email: (d.data() as any).email, adminLevel: (d.data() as any).adminLevel }));
}

// ===== Regras de negócio / validações no backend =====
export interface RuleViolationLog {
  uid?: string;
  councilorId: string;
  institutionId?: string;
  attemptedValue?: number;
  currentAllocated?: number;
  totalLimit?: number;
  type: 'limit_exceeded' | 'duplicate_attempt';
  message: string;
  dateTime: string; // ISO
}

export async function logRuleViolation(log: RuleViolationLog) {
  try {
    await addDoc(violationLogsCol, log);
  } catch (e) {
    console.warn('Falha ao registrar log de violação', e);
  }
}

export interface AdminActionLog {
  actorUid: string;
  actorEmail?: string;
  action: 'delete' | 'create' | 'update';
  entity: 'councilor' | 'institution' | 'amendment' | 'user' | 'template';
  entityId: string;
  entityName?: string;
  dateTime: string; // ISO
}

export async function logAdminAction(entry: AdminActionLog) {
  try {
    await addDoc(adminLogsCol, entry);
  } catch (e) {
    console.warn('Falha ao registrar ação administrativa', e);
  }
}

export async function getCouncilor(id: string): Promise<Councilor | null> {
  const ref = doc(db, 'councilors', id);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<Councilor, 'id'>) }) : null;
}

export async function deleteCouncilor(id: string) {
  const ref = doc(db, 'councilors', id);
  return deleteDoc(ref);
}

export interface UploadLog {
  uid?: string;
  email?: string;
  institutionId?: string;
  fileName?: string;
  fileSize?: number;
  contentType?: string;
  outcome: 'success' | 'failure';
  reason?: string;
  dateTime: string; // ISO
}

export async function logUploadAttempt(log: UploadLog) {
  try {
    await addDoc(uploadLogsCol, log);
  } catch (e) {
    console.warn('Falha ao registrar upload', e);
  }
}

// Cria ou mescla emenda de forma segura: impede exceder limite e evita duplicidade por instituição
export async function addOrMergeAmendmentSecure(a: Omit<Amendment, 'id'>): Promise<{ action: 'created' | 'merged'; id: string; newValue?: number; previousValue?: number; }> {
  const { councilorId, institutionId, value } = a;
  if (!councilorId || !institutionId) throw new Error('missing-fields');
  const numericValue = Number(value) || 0;
  if (numericValue <= 0) throw new Error('invalid-value');

  const councilor = await getCouncilor(councilorId);
  if (!councilor) throw new Error('councilor-not-found');

  // Leitura das emendas do vereador
  const existingAmendments = await listAmendmentsByCouncilor(councilorId);
  const currentAllocated = existingAmendments.reduce((sum, it) => sum + (Number(it.value) || 0), 0);
  const existingForInstitution = existingAmendments.find((it) => it.institutionId === institutionId) || null;

  // Verificações de limite considerando mescla
  const proposedIncrease = numericValue; // aumento no total alocado
  const wouldExceed = currentAllocated + proposedIncrease > councilor.totalLimit;
  if (wouldExceed) {
    await logRuleViolation({
      uid: auth.currentUser?.uid,
      councilorId,
      institutionId,
      attemptedValue: numericValue,
      currentAllocated,
      totalLimit: councilor.totalLimit,
      type: 'limit_exceeded',
      message: 'Tentativa de alocar valor que excede o limite disponível.',
      dateTime: new Date().toISOString(),
    });
    throw new Error('limit-exceeded');
  }

  if (existingForInstitution) {
    // Mescla: soma valor ao existente
    const ref = doc(db, 'amendments', existingForInstitution.id);
    const previousValue = Number(existingForInstitution.value) || 0;
    const newValue = previousValue + numericValue;
    await updateDoc(ref, { value: newValue });
    return { action: 'merged', id: existingForInstitution.id, newValue, previousValue };
  }

  // Criação de nova emenda
  const created = await addDoc(amendmentsCol, a);
  return { action: 'created', id: created.id };
}

// ===== CRUD de modelos de descrição =====
export async function listDescriptionTemplates(): Promise<DescriptionTemplate[]> {
  const snap = await getDocs(descriptionTemplatesCol);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DescriptionTemplate, 'id'>) }));
}

export async function addDescriptionTemplate(t: Omit<DescriptionTemplate, 'id'>) {
  try {
    return await addDoc(descriptionTemplatesCol, t);
  } catch (e) {
    console.warn('Firestore SDK addDescriptionTemplate falhou, tentando REST fallback...', e);
    await restAddDescriptionTemplate(t);
  }
}

export async function updateDescriptionTemplate(id: string, data: Partial<DescriptionTemplate>) {
  try {
    const ref = doc(db, 'description_templates', id);
    return await updateDoc(ref, data);
  } catch (e) {
    console.warn('Firestore SDK updateDescriptionTemplate falhou, tentando REST fallback...', e);
    await restUpdateDescriptionTemplate(id, data);
  }
}

export async function deleteDescriptionTemplate(id: string) {
  try {
    const ref = doc(db, 'description_templates', id);
    return await deleteDoc(ref);
  } catch (e) {
    console.warn('Firestore SDK deleteDescriptionTemplate falhou, tentando REST fallback...', e);
    await restDeleteDescriptionTemplate(id);
  }
}

// ===== REST fallback para evitar aborts de WebChannel em ambientes com proxy =====
async function getIdTokenOrThrow() {
  const user = auth.currentUser;
  if (!user) throw new Error('not-authenticated');
  return user.getIdToken();
}

function docNameToId(name: string): string {
  const parts = name.split('/');
  return parts[parts.length - 1];
}

const PROJECT_ID = (import.meta.env.VITE_FIREBASE_PROJECT_ID as string) || '';

async function restAddDescriptionTemplate(t: Omit<DescriptionTemplate, 'id'>) {
  const token = await getIdTokenOrThrow();
  const url = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents/description_templates`;
  const fields: any = { text: { stringValue: t.text } };
  if (typeof t.active !== 'undefined') fields.active = { booleanValue: !!t.active };
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) {
    const msg = await safeText(res);
    throw new Error(`REST addDescriptionTemplate failed: ${res.status} ${msg}`);
  }
  const json = await res.json();
  return docNameToId(json.name as string);
}

async function restUpdateDescriptionTemplate(id: string, data: Partial<DescriptionTemplate>) {
  const token = await getIdTokenOrThrow();
  const base = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents/description_templates/${encodeURIComponent(id)}`;
  const masks: string[] = [];
  const fields: any = {};
  if (typeof data.text !== 'undefined') {
    masks.push('text');
    fields.text = { stringValue: data.text };
  }
  if (typeof data.active !== 'undefined') {
    masks.push('active');
    fields.active = { booleanValue: !!data.active };
  }
  const maskParams = masks.map((f) => `updateMask.fieldPaths=${encodeURIComponent(f)}`).join('&');
  const url = maskParams ? `${base}?${maskParams}` : base;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) {
    const msg = await safeText(res);
    throw new Error(`REST updateDescriptionTemplate failed: ${res.status} ${msg}`);
  }
}

async function restDeleteDescriptionTemplate(id: string) {
  const token = await getIdTokenOrThrow();
  const url = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents/description_templates/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const msg = await safeText(res);
    throw new Error(`REST deleteDescriptionTemplate failed: ${res.status} ${msg}`);
  }
}

async function safeText(res: Response) {
  try {
    return await res.text();
  } catch {
    return '';
  }
}

// ===== REST fallback: leitura de users/{uid} quando SDK sofre abort (WebChannel) =====
export async function restGetUserMapping(uid: string): Promise<{ role?: UserRole; councilorId?: string; institutionId?: string } | undefined> {
  try {
    const token = await getIdTokenOrThrow();
    const url = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents/users/${encodeURIComponent(uid)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return undefined;
    const json = await res.json();
    const f = (json.fields || {}) as Record<string, any>;
    const role = (f.role?.stringValue || f.role?.mapValue || f.role?.value) as UserRole | undefined;
    const councilorId = (f.councilorId?.stringValue) as string | undefined;
    const institutionId = (f.institutionId?.stringValue) as string | undefined;
    return { role, councilorId, institutionId };
  } catch {
    return undefined;
  }
}

export async function restListAmendmentsByInstitutionIds(ids: string[]): Promise<Amendment[]> {
  const uniq = Array.from(new Set(ids)).filter(Boolean);
  if (uniq.length === 0) return [];
  const token = await getIdTokenOrThrow();
  const url = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents:runQuery`;
  const body = {
    structuredQuery: {
      from: [{ collectionId: 'amendments' }],
      where: {
        fieldFilter: {
          field: { fieldPath: 'institutionId' },
          op: 'IN',
          value: { arrayValue: { values: uniq.map((id) => ({ stringValue: id })) } },
        },
      },
    },
  } as any;
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  if (!res.ok) {
    const msg = await safeText(res);
    throw new Error(`REST listAmendmentsByInstitutionIds failed: ${res.status} ${msg}`);
  }
  const arr = await res.json();
  const out: Amendment[] = [];
  for (const item of arr) {
    const doc = item.document;
    if (!doc || !doc.fields) continue;
    const f = doc.fields as any;
    const id = doc.name?.split('/').pop();
    out.push({
      id,
      councilorId: f.councilorId?.stringValue || '',
      institutionId: f.institutionId?.stringValue || '',
      description: f.description?.stringValue || '',
      officialCode: f.officialCode?.stringValue,
      value: Number(f.value?.integerValue || f.value?.doubleValue || 0),
      status: f.status?.stringValue || '',
      date: f.date?.stringValue || '',
      category: f.category?.stringValue,
      classification: f.classification?.stringValue,
      objectDetail: f.objectDetail?.stringValue,
      workPlan: f.workPlan?.mapValue ? {
        projectName: f.workPlan.mapValue.fields?.projectName?.stringValue,
        purpose: f.workPlan.mapValue.fields?.purpose?.stringValue,
        period: f.workPlan.mapValue.fields?.period?.stringValue,
      } : undefined,
      attachments: f.attachments?.arrayValue?.values?.map((v: any) => v.stringValue) || [],
      signedLink: f.signedLink?.stringValue,
      dispatchStatus: f.dispatchStatus?.stringValue,
      dispatchNote: f.dispatchNote?.stringValue,
    } as Amendment);
  }
  return out;
}

// ===== Histórico de alterações de emendas =====
export async function logAmendmentHistory(
  amendmentId: string,
  entry: { actorUid: string; actorEmail?: string; dateTime: string; changes: Array<{ field: string; from?: any; to?: any }> }
) {
  try {
    const col = collection(db, 'amendments', amendmentId, 'history');
    await addDoc(col, entry as any);
  } catch (e) {
    console.warn('Falha ao registrar histórico da emenda', e);
  }
}

export async function getAmendmentHistory(amendmentId: string) {
  try {
    const col = collection(db, 'amendments', amendmentId, 'history');
    const q = query(col, orderBy('dateTime', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as { actorUid: string; actorEmail?: string; dateTime: string; changes: Array<{ field: string; from?: any; to?: any }> });
  } catch (e) {
    console.warn('Falha ao buscar histórico da emenda', e);
    return [];
  }
}
